import type { IBootstrapAxisValue, IWorkfrontTarget } from '@atlas/types'
import { WorkfrontBundleObjectRole, WorkfrontTargetCardinality, WorkfrontTargetLevel } from '@atlas/types'
import type { SelectFieldOption } from '@atlas/ui-components'
import type { FieldErrors } from '../validation'

/** What the edit drawer offers to pick from, loaded by the tab that opens it. */
export interface DrawerContext {
	catalogue: IWorkfrontTarget[]
	/** Workfront's custom fields, from the reference copy: ID → label with its type. */
	parameters: SelectFieldOption[]
	/** The values of the edited value row's axis, in hierarchy order. */
	axisValues: IBootstrapAxisValue[]
	geographyValues: IBootstrapAxisValue[]
	/** Asset type code → its name. */
	assetTypeNames: Record<string, string>
	/** The classification editor for the value row's axis, pre-filled with its label. */
	createValueHref: string | null
	/** The active users whose job function is PM, for a PM rule. */
	pms: SelectFieldOption[]
}

export const EMPTY_DRAWER_CONTEXT: DrawerContext = {
	catalogue: [],
	parameters: [],
	axisValues: [],
	geographyValues: [],
	assetTypeNames: {},
	createValueHref: null,
	pms: [],
}

/** One kind's form: the row, a patch to it, and the messages under its fields. */
export interface FormProps<T> {
	row: T
	onChange: (patch: Partial<T>) => void
	errors: FieldErrors
	context: DrawerContext
}

/** The target level a source role's fields map to, as save-time validation checks it. */
export const LEVEL_OF_ROLE: Record<string, WorkfrontTargetLevel> = {
	[WorkfrontBundleObjectRole.PARENT]: WorkfrontTargetLevel.PARENT,
	[WorkfrontBundleObjectRole.GENERATOR]: WorkfrontTargetLevel.PARENT,
	[WorkfrontBundleObjectRole.CHILD]: WorkfrontTargetLevel.CHILD,
	[WorkfrontBundleObjectRole.TASK]: WorkfrontTargetLevel.TASK,
	[WorkfrontBundleObjectRole.NOTE]: WorkfrontTargetLevel.COMMENT,
	[WorkfrontBundleObjectRole.DOCUMENT]: WorkfrontTargetLevel.DOCUMENT,
}

/** `Species › subspecies · child.tuple.species · axis`, with `(many)` for a multi-valued target. */
export const targetOptionLabel = (target: IWorkfrontTarget): string =>
	`${target.label} · ${target.path} · ${target.type}${target.cardinality === WorkfrontTargetCardinality.MANY ? ' (many)' : ''}${target.required ? ' · required' : ''}`

/** `Brand › Bravecto › Bravecto Plus`: a value's path from its axis root. */
export function valuePath(values: IBootstrapAxisValue[], valueId: string | null | undefined): string | null {
	const byId = new Map(values.map((value) => [value.valueId, value]))
	const labels: string[] = []
	let current = valueId ? byId.get(valueId) : undefined
	while (current && labels.length < 20) {
		labels.unshift(current.label)
		current = current.parentId ? byId.get(current.parentId) : undefined
	}
	return labels.length ? labels.join(' › ') : null
}

/** An axis's values as indented picker options. */
export const valueOptions = (values: IBootstrapAxisValue[]): SelectFieldOption[] =>
	values.map((value) => ({ value: value.valueId, label: value.label, depth: value.depth }))
